// @flow

import * as React from 'react';
import numeral from 'numeral';
import { TimelineMax, Power4 } from 'gsap';

import RewriteNavLink from 'app/components/routing/RewriteNavLink';
import * as cssUtils from 'app/utils/cssUtils';
import t from 'app/utils/translate';
import Theme from 'app/theme';
import AnimateOnChange from 'app/components/commons/AnimateOnChange';
import useSheet from 'app/jss';
import StatusDot from 'app/components/commons/StatusDot';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';

type Props = {
  classes: Object,
  link: string,
  name: string,
  points: number,
  position: number,
  status?: number,
  winnings?: number
};

class LiveChallengeRow extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    status: 0,
    winnings: 0
  };

  handleNumberChange = (node, delay = 0) => {
    const tl = new TimelineMax({ delay });
    tl.to(node, 0.1, {
      css: {
        color: Theme.color.warning,
        scale: 1.2,
        display: 'inline-block'
      }
    }).to(node, 1, {
      css: { color: Theme.color.white, scale: 1 },
      ease: Power4.easeIn,
      clearProps: 'color,scale,display',
      delay: 0.5
    });
    return tl;
  };

  render() {
    const { classes, link, name, points, position, status, winnings } = this.props;
    return (
      <RewriteNavLink to={link} className={classes.row} activeClassName={classes.row__active}>
        <div className={classes.dotParent}>
          <StatusDot statusId={status} />
        </div>
        <div className={classes.title}>{name}</div>
        <div className={classes.stats}>
          <div className={classes.stat}>
            <div className={classes.stat_title}>{t('Pos')}</div>
            <div className={classes.stat_data}>
              <AnimateOnChange animation={this.handleNumberChange} value={position}>
                {position}
              </AnimateOnChange>
            </div>
          </div>
          <div className={classes.stat}>
            <div className={classes.stat_title}>{t('Win')}</div>
            <div className={classes.stat_data}>
              <AnimateOnChange animation={this.handleNumberChange} value={winnings}>
                <CurrencyIcon />
                {numeral(winnings).format('0,0.[00]')}
              </AnimateOnChange>
            </div>
          </div>
          <div className={classes.stat}>
            <div className={classes.stat_title}>{t('Pts')}</div>
            <div className={classes.stat_data}>
              <AnimateOnChange animation={this.handleNumberChange} value={points}>
                {numeral(points).format('0.[00]')}
              </AnimateOnChange>
            </div>
          </div>
        </div>
      </RewriteNavLink>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    row: {
      borderBottom: `1px solid ${theme.color.basis1}`,
      borderLeft: '2px solid transparent',
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing.small,
      paddingRight: theme.spacing.default,
      textTransform: 'uppercase',
      transition: 'all 0.2s',
      position: 'relative',
      ...cssUtils.hover({
        backgroundColor: theme.color.basis3,
        textDecoration: 'none'
      }),
      '&:active, &:focus': {
        textDecoration: 'none'
      }
    },
    row__active: {
      borderLeftColor: theme.color.primary
    },
    title: {
      fontSize: theme.fontSize.xsmall
    },
    stats: {
      alignItems: 'center',
      display: 'flex',
      fontSize: theme.fontSize.tiny,
      marginTop: theme.spacing.tiny,
      textTransform: 'uppercase'
    },
    stat: {
      paddingRight: theme.spacing.small
    },
    stat_title: {
      color: theme.color.basis5
    },
    stat_data: {
      color: theme.color.white,
      transform: 'translateZ(0)'
    },
    dotParent: {
      right: theme.spacing.tiny,
      top: 16,
      position: 'absolute'
    }
  };
}

export default useSheet(applyStyles)(LiveChallengeRow);
